import styles from "./credit-card-payoff.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as finance from "shared/finance"
import Line from "activities/components/charts/line"
import AccessibilityTable from "activities/components/shared/accessibility-table"

const reduceOneMonth = (balance, rate, payment) => balance * (rate + 1) - payment
const reduceOneYear = (balance, rate, payment) => [...Array(12)].reduce((memo) => reduceOneMonth(memo, rate, payment), balance)
const transformMonths = (months) => (!months ? 10 : months === Infinity ? 10 : Math.ceil(months / 12))
const periodTypeIndex = (monthsToPayoff) => ({
  months: {
    label: "Months",
    tooltipTitle: "Month",
    periods: monthsToPayoff,
    reduceFunc: reduceOneMonth
  },
  years: {
    label: "Years",
    tooltipTitle: "Year",
    periods: transformMonths(monthsToPayoff),
    reduceFunc: reduceOneYear
  }
})

const chartData = (label, tooltipTitle, data) => ({
  legend: {
    x: {
      key: "period",
      label: label
    },
    y: {
      label: "Balance",
      type: "currency"
    }
  },
  fields: {
    period: {type: "number", tooltip: {title: tooltipTitle, order: 0}},
    balance: {type: "currency", tooltip: {title: "Balance", order: 1, color: "color-1"}}
  },
  values: {
    x: util.xAxisArray(data.length),
    y: data
  }
})

const getDataForRecharts = (startingAmount, rate, payment, periods, reduceFunc) => {
  let balance = startingAmount
  return util.xAxisArray(periods).map((period) => {
    if (period > 0) {
      balance = util.ifNegative(reduceFunc(balance, rate, payment))
    }
    return {period, balance}
  })
}

const getChartData = (startingAmount, rate, payment, monthsToPayoff) => {
  let periodType = null
  if (monthsToPayoff > 36 || !monthsToPayoff) periodType = "years"
  else if (monthsToPayoff <= 36) periodType = "months"
  const index = periodTypeIndex(monthsToPayoff)
  const {label, tooltipTitle, periods, reduceFunc} = index[periodType]
  return chartData(label, tooltipTitle, getDataForRecharts(startingAmount, rate, payment, periods, reduceFunc))
}

const calcThings = ({rate, balance, monthlyPayment}) => {
  const decimalRate = rate / 100
  const monthlyInterestRate = util.getMonthlyRate(rate > 1 ? decimalRate : rate)
  const monthsToPayoff = finance.calcMonthsToPayoffFixedLoan(balance, monthlyInterestRate, monthlyPayment)
  const canBePaidOff = finance.canBePaidOff({balance, rate: decimalRate, payment: monthlyPayment})
  const schedule = canBePaidOff ? finance.genFixedPaymentSchedule(balance, decimalRate, monthlyPayment) : null
  const totalPaid = canBePaidOff ? monthlyPayment * (monthsToPayoff - 1) + schedule[schedule.length - 2] : null
  const totalInterest = totalPaid - balance
  const payoffDate = util.getPayoffDateObject(monthsToPayoff)
  return {
    monthlyInterestRate,
    monthsToPayoff,
    totalPaid,
    totalInterest,
    payoffDate
  }
}

const show = (payoffDate, val, alt) => (payoffDate.month ? val : alt)

const showFull = (payoffDate) => show(payoffDate, `${payoffDate.month} ${payoffDate.year}`, "Not Possible")

const showMonths = (payoffDate, monthsToPayoff) => show(payoffDate, monthsToPayoff, "Unknown")

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {balance, monthlyPayment, rate, resetKey} = state
    const {id, theme} = getProps()
    const {monthlyInterestRate, monthsToPayoff, totalPaid, totalInterest, payoffDate} = calcThings(state)

    const yearData =
      finance.canBePaidOff({balance, rate: rate / 100, payment: monthlyPayment}) &&
      finance.genFixedPaymentSchedule(balance, rate / 100, monthlyPayment).reduce((memo, item, idx) => {
        if (idx % 12 === 0) {
          memo.push(item)
        }

        if (idx === monthsToPayoff) {
          memo.push(item)
        }

        return memo
      }, [])

    const accessibilityTableHeadings = [
      {
        name: "Years",
        key: "years"
      },
      {
        name: "Balance",
        key: "balance"
      }
    ]

    const accessibilityTableData =
      yearData &&
      yearData.map((row, idx) => ({
        years: idx,
        balance: util.formatDollarsNoCents(row)
      }))

    const accessibilityTableLabel = "Credit Card Payoff Schedule"

    return (
      <div>
        <shared.SummaryCalcContainer>
          <shared.InputsContainer>
            <inputs.Input id={id} size="big" title="Current Balance" value={balance} onChange={updateState("balance", util.fromString)} inputMode="decimal" />
            <inputs.Input id={id} title="Monthly Payment" value={monthlyPayment} onChange={updateState("monthlyPayment", util.fromString)} inputMode="decimal" />
            <inputs.Input id={id} title="Interest Rate" valueType="percent" value={rate} onChange={updateState("rate")} inputMode="decimal" />
          </shared.InputsContainer>
          <shared.ChartContainer role="img" aria-label="Line chart showing when the credit card will be paid off">
            <Line
              data={getChartData(balance, monthlyInterestRate, monthlyPayment, monthsToPayoff)}
              theme={theme}
              aria-label="This line graph visually represents how long it will take to pay off your credit card. Read below to find the pay-off date, how many months to pay off, the total amount paid, and the total interest to be paid."
            />
          </shared.ChartContainer>
          <shared.SummaryContainer className={styles["summary"]}>
            <callouts.Callout>
              <callouts.Detail label="Payoff Date" result={showFull(payoffDate)} dataTestId="payoff" />
              <callouts.Detail label="Months To Payoff" result={showMonths(payoffDate, monthsToPayoff)} dataTestId="months-to-payoff" />
              <callouts.Detail label="Total Amount Paid" result={show(payoffDate, util.formatDollarsNoCents(totalPaid), "Unknown")} dataTestId="amount-paid" />
              <callouts.Detail label="Total Interest Paid" result={show(payoffDate, util.formatDollarsNoCents(totalInterest), "Unknown")} dataTestId="interest-paid" />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {payoffDate, totalPaid, totalInterest, monthsToPayoff} = calcThings(state)
      return Object.assign(state, {
        payoffDate: showFull(payoffDate),
        monthsToPayoff: showMonths(payoffDate, monthsToPayoff),
        totalAmountPaid: util.round(totalPaid),
        totalInterestPaid: util.round(totalPaid)
      })
    },
    defaultProps: {
      id: "credit-card-payoff",
      className: "credit-card-payoff",
      title: "Credit Card Payoff Calculator",
      subtitle: "Explore what it would take to payoff your credit card."
    },
    defaultState: {
      balance: 10000,
      monthlyPayment: 150,
      rate: 21
    }
  }
)
